import { useState } from "react"
import { Button } from "@/components/ui/button"
import { AppointmentDialogFooter } from "../AppointmentDialogFooter"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import {
  MAX_LINK_REASON_LENGTH,
  buildRescheduleLinkBody,
  describeRescheduleLinkFailure,
  useLinkRescheduledBookingMutation,
} from "../../api/linkApi"
import type { BookingRequestListItem } from "../../bookingCalendarApi"
import { useGetRescheduleCandidatesQuery } from "../../bookingCalendarApi"
import { statusLabel } from "../../lib/bookingStatus"
import type { BookingAction, BookingActionContext, BookingActionFormProps } from "./types"
import { useReportBusy } from "./useReportBusy"

/**
 * Administrators only, and only for a booking CMS has a request for: the link lives on the request row.
 * Not limited to upcoming bookings — fixing the history of a past one is the usual case.
 */
export function canLinkRescheduled(ctx: BookingActionContext): boolean {
  return ctx.canManage && Boolean(ctx.requestId)
}

/**
 * An administrator linking this booking by hand to the one it was rescheduled from or to. A new link
 * replaces any existing one, and the "from" booking becomes Rescheduled.
 */
export const LinkRescheduledAction: BookingAction = {
  id: "linkingReschedule",
  label: "Link rescheduled booking",
  triggerVariant: "outline",
  isAvailable: canLinkRescheduled,
  Form: LinkRescheduledForm,
}

const NO_CANDIDATE = "__none__"

function candidateLabel(candidate: BookingRequestListItem, timeZone: string): string {
  const when = candidate.slotStart
    ? new Date(candidate.slotStart).toLocaleString("en-US", {
        timeZone,
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "no time"
  const who = candidate.attendeeName ? ` · ${candidate.attendeeName}` : ""
  return `${candidate.referenceCode} · ${when} · ${statusLabel(candidate.status)}${who}`
}

function LinkRescheduledForm({ ctx, onDone, onBusyChange }: BookingActionFormProps) {
  const [linkBooking, linkState] = useLinkRescheduledBookingMutation()
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  const [reason, setReason] = useState("")
  const [error, setError] = useState<string | null>(null)

  const busy = linkState.isLoading
  useReportBusy(onBusyChange, busy)

  const { requestId, statusView } = ctx
  const candidates = useGetRescheduleCandidatesQuery(requestId ?? "", { skip: !requestId })
  const candidateList = candidates.data ?? []
  const chosenCandidate = candidateList.find(c => c.referenceCode === to.trim())

  const submittable = Boolean(requestId) && (from.trim() || to.trim()) && reason.trim().length > 0

  async function submit() {
    if (!requestId) return

    const built = buildRescheduleLinkBody({ requestId, from, to, reason })
    if ("error" in built) {
      setError(built.error)
      return
    }

    setError(null)
    const result = await linkBooking({ requestId, ...built.body })
    if ("error" in result) {
      setError(describeRescheduleLinkFailure(result.error))
      return
    }

    // Closes back to the booking: its status section refetches and shows the new link.
    onDone()
  }

  return (
    <>
      <section className="space-y-3">
        {(statusView?.rescheduledFromCalBookingUid || statusView?.rescheduledToCalBookingUid) && (
          <p className="text-xs text-muted-foreground">
            Currently
            {statusView?.rescheduledFromCalBookingUid && <> rescheduled from booking {statusView.rescheduledFromCalBookingUid}</>}
            {statusView?.rescheduledFromCalBookingUid && statusView?.rescheduledToCalBookingUid && ";"}
            {statusView?.rescheduledToCalBookingUid && <> rescheduled to booking {statusView.rescheduledToCalBookingUid}</>}
            . A new link replaces it.
          </p>
        )}

        <div>
          <Label htmlFor="link-from" className="text-xs font-medium">
            Rescheduled from <span className="text-muted-foreground">(reference code or request ID)</span>
          </Label>
          <Input
            id="link-from"
            className="mt-1.5"
            value={from}
            disabled={busy}
            placeholder="The booking this one replaced"
            onChange={event => setFrom(event.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="link-to" className="text-xs font-medium">
            Rescheduled to <span className="text-muted-foreground">(reference code or request ID)</span>
          </Label>
          {candidateList.length > 0 && (
            <Select
              value={chosenCandidate ? chosenCandidate.referenceCode : NO_CANDIDATE}
              onValueChange={value => setTo(value === NO_CANDIDATE ? "" : value)}
              disabled={busy}
            >
              <SelectTrigger id="link-to-candidates" aria-label="Same-household bookings" className="mt-1.5 w-full">
                <SelectValue placeholder="Choose a same-household booking" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CANDIDATE}>Choose a same-household booking</SelectItem>
                {candidateList.map(candidate => (
                  <SelectItem key={candidate.id} value={candidate.referenceCode}>
                    {candidateLabel(candidate, ctx.timeZone)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Input
            id="link-to"
            className="mt-1.5"
            value={to}
            disabled={busy}
            placeholder="The booking that replaced this one"
            onChange={event => setTo(event.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="link-reason" className="text-xs font-medium">
            Reason <span className="text-muted-foreground">(required, staff only)</span>
          </Label>
          <Textarea
            id="link-reason"
            className="mt-1.5"
            rows={2}
            maxLength={MAX_LINK_REASON_LENGTH}
            value={reason}
            disabled={busy}
            placeholder="e.g. Household rebooked by phone"
            onChange={event => setReason(event.target.value)}
          />
        </div>

        <p className="text-xs text-muted-foreground">
          The earlier booking is marked Rescheduled, and cancelled in the calendar if it is still booked and to
          come. A booking that was already cancelled is not cancelled again.
        </p>
      </section>

      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}

      <AppointmentDialogFooter>
        <Button variant="outline" disabled={busy} onClick={onDone}>
          Cancel
        </Button>
        <Button disabled={busy || !submittable} onClick={submit}>
          {busy ? "Saving…" : "Save link"}
        </Button>
      </AppointmentDialogFooter>
    </>
  )
}
